import emailjs from "@emailjs/browser";
import { useState } from "react";
import { CheckCircle2, Clock, Mail } from "lucide-react";
import { Badge } from "../Components/ui/Badge";
import { Button } from "../Components/ui/Button";
import { Input } from "../Components/ui/Input";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userId = process.env.REACT_APP_EMAILJS_USER_ID;

    if (!serviceId || !templateId || !userId) {
      setError("Email service is not configured. Please email us directly.");
      return;
    }

    emailjs.send(serviceId, templateId, formData, userId).then(
      () => {
        setSubmitted(true);
        setFormData({ name: "", email: "", service: "", message: "" });
      },
      () => {
        setError("Failed to send message. Please try again.");
      }
    );
  };

  return (
    <section id="contact" className="border-b border-zinc-200 bg-brand-surface py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Badge className="mb-6 rounded-full border-0 bg-violet-50 px-4 py-1 text-brand-purple">
              Get in touch
            </Badge>
            <h2 className="mb-6 font-display text-4xl font-bold tracking-tight text-brand-ink md:text-5xl">
              Let&apos;s build something <span className="text-brand-purple">extraordinary.</span>
            </h2>
            <p className="mb-12 text-xl text-brand-muted">
              Whether you need an enterprise platform or a sleek mobile app, our team is ready to deliver.
            </p>

            <div className="mb-8 flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-brand-purple">
                <Mail size={20} />
              </div>
              <div>
                <h4 className="mb-1 font-display font-bold text-brand-ink">Email us</h4>
                <a href="mailto:info@quantumsparktechnology.com" className="font-medium text-brand-purple hover:underline">
                  info@quantumsparktechnology.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-brand-purple">
                <Clock size={20} />
              </div>
              <div>
                <h4 className="mb-2 font-display font-bold text-brand-ink">Response time</h4>
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-600">
                  Typically replies within 24 hours
                </Badge>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-zinc-200 bg-white p-8 shadow-xl shadow-zinc-200/50 md:p-10">
            {submitted ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                  <CheckCircle2 size={40} />
                </div>
                <h3 className="mb-4 font-display text-2xl font-bold text-brand-ink">Message sent!</h3>
                <p className="mb-8 text-brand-muted">Thanks! We&apos;ve received your request and will be in touch within 24 hours.</p>
                <Button variant="outline" className="rounded-full" onClick={() => setSubmitted(false)}>
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-brand-ink" htmlFor="contact-name">
                      Full Name
                    </label>
                    <Input
                      id="contact-name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="border-zinc-200 bg-zinc-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-brand-ink" htmlFor="contact-email">
                      Email
                    </label>
                    <Input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="border-zinc-200 bg-zinc-50"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-brand-ink" htmlFor="contact-service">
                    Service
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="Website Development">Website Development</option>
                    <option value="Mobile App Development">Mobile App Development</option>
                    <option value="Artificial Intelligence Development">Artificial Intelligence Development</option>
                    <option value="Blockchain Development">Blockchain Development</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="Quality Assurance">Quality Assurance</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-brand-ink" htmlFor="contact-message">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    className="flex w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/30"
                  />
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <Button type="submit" className="h-12 w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple-dark">
                  Submit inquiry
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
